import {
  DndContext,
  DragOverlay,
  MouseSensor,
  pointerWithin,
  useSensor,
} from "@dnd-kit/core";
import { useDispatch, useSelector } from "react-redux";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { DATA_PAGE } from "helpers/constants/pages";
import {
  setNavigationCluster,
  setTemporaryExportData,
  updateTemporaryExportData,
} from "redux/SingleDataset/actions";
import DataList, { Cluster } from "views/pages/DataPage/CardsView/DataList";
import MetadataSummary from "views/components/MetadataSummary";
import DataPageFilters from "views/pages/DataPage/CardsView/DataPageFilters";
import styles from "./style.module.scss";
import LabelPropagation from "views/components/LabelPropagation";
import {
  getTemporaryExportData,
  getDataset,
} from "redux/SingleDataset/selectors";
import { useLocation, useParams } from "react-router";
import { Option } from "views/uikit/FormFields/SelectList";
import {
  FLOW_PARAMETER,
  REVIEW_PARAMETER,
  SEED_PARAMETER,
} from "../../../../helpers/constants/miscellaneous";
import ReviewList from "./ReviewList";
import ViewToggleButtons from "views/components/ViewToggleButtons";
import classNames from "classnames";
import { createPortal } from "react-dom";
import EnrichmentCard, { EnrichmentCardProps } from "./DataList/EnrichmentCard";
import { useFlywheelStatus } from "../../../../hooks/LabelPropagation/useFlywheelStatus";
import ChatView from "../../../components/ChatView/ChatView";
import {
  RightPanelMode,
  useDataPageRightPanelContext,
} from "../../../providers/DataPageRightPanelProvider";
import { useClearLabelPropagationSearchParams } from "../../../../hooks/LabelPropagation/useClearLabelPropagationSearchParams";
import QueryModalWrapper from "./QueryModalWrapper";
import { useFeatureFlagService } from "../../../../hooks/useFeatureFlagService";

interface CardsViewProps {
  clusters?: Cluster[];
  dynamicPage: string;
  navigationCluster: any;
  isCardListLoading: boolean;
  navigationClusterLoader: boolean;
  metadataSummaryEnabled: boolean;
  isImageUploading: boolean;
  initialRightPanelMode?: "summary" | "labels" | null;
  setReviewCounters: (text: ReactElement | null) => void;
  reviewCounters: ReactElement | null;
}

type DataListPageType = "review" | "data";
const CardsView = ({
  clusters,
  dynamicPage,
  navigationCluster,
  isCardListLoading,
  navigationClusterLoader,
  metadataSummaryEnabled,
  isImageUploading,
  setReviewCounters,
  reviewCounters,
}: CardsViewProps) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [activeDragItem, setActiveDragItem] = useState<{
    exportType: string;
    props: EnrichmentCardProps;
  } | null>(null);

  const { datasetId, clusterId } = useParams();
  const isSeed = searchParams.get(SEED_PARAMETER);
  const { mode: rightPanelMode, setMode: setRightPanelMode } =
    useDataPageRightPanelContext();
  const [shouldFetch, setShouldFetch] = useState<string>();
  const [labelsOptions, setLabelOptions] = useState<Option[]>();
  const [displayNewFloatingPanel, setDisplayNewFloatingPanel] =
    useState<boolean>(false);
  const [selectedLabel, setSelectedLabel] = useState<string>();
  const [assignItemsToClass, setAssignItemsToClass] = useState<
    ((className: string) => void) | null
  >(null);

  const flywheelStatus = useFlywheelStatus(datasetId);

  const [isMetadataSummaryOpen, setIsMetadataSummaryOpen] = useState(true);
  const dispatch = useDispatch();
  const showLabelPropagation = useFeatureFlagService("FLYWHEEL").show;
  const dataset = useSelector(getDataset);

  const labelPropagationProps = useMemo(
    () => ({
      onLabelClick: (value: string[]) => {
        setSelectedLabel(value.toString());
        if (assignItemsToClass && value.toString()) {
          assignItemsToClass(value.toString());
        }
      },
      options: labelsOptions,
      displayNewFloatingPanel:
        displayNewFloatingPanel && rightPanelMode === "labels",
      showLabelPropagation: showLabelPropagation,
    }),
    [
      labelsOptions,
      displayNewFloatingPanel,
      rightPanelMode,
      assignItemsToClass,
      showLabelPropagation,
    ],
  );
  const isDataPage = dynamicPage === DATA_PAGE;
  const showMetadataSummary = isMetadataSummaryOpen && metadataSummaryEnabled;
  const dynamicLoader = isDataPage
    ? isCardListLoading
    : navigationClusterLoader;

  useEffect(() => {
    if (isImageUploading && isMetadataSummaryOpen) {
      setIsMetadataSummaryOpen(false);
    } else if (!isImageUploading && !isMetadataSummaryOpen) {
      setIsMetadataSummaryOpen(true);
    }

    return () => {
      if (!isSeed) {
        dispatch(setTemporaryExportData(null));
      }
    };
    //eslint-disable-next-line
  }, [isImageUploading]);

  const showSummaryTab = useCallback(
    () => setRightPanelMode("summary"),
    [setRightPanelMode],
  );

  const getRightPanel = () => {
    // Always show the unified right panel
    return (
      <>
        {datasetId && rightPanelMode === "summary" && <MetadataSummary />}
        {datasetId && rightPanelMode === "chat" && <ChatView />}
        {datasetId && rightPanelMode === "labels" && (
          <LabelPropagation
            datasetId={datasetId}
            assignItemsToClass={selectedLabel}
            onAssign={(assignFunc) => setAssignItemsToClass(() => assignFunc)}
            onDataChange={(value) => {
              setDisplayNewFloatingPanel(value.displayNewFloatingPanel);
              setLabelOptions(value.classesOptions);
            }}
            resetShouldFetch={onNavigateToCluster}
            shouldFetch={shouldFetch}
            flywheelStatus={flywheelStatus}
            showSummaryTab={showSummaryTab}
            datasetStatus={dataset?.status}
          />
        )}
      </>
    );
  };

  const getDataPageFilters = () => {
    if (!isReview) return <DataPageFilters />;
  };

  const updateNavigationCluster = useCallback(
    (data: any) => {
      dispatch(setNavigationCluster(data));
    },
    [dispatch],
  );

  const onNavigateToCluster = useCallback(() => {
    setShouldFetch(undefined);
    dispatch(setTemporaryExportData(null));
  }, [dispatch]);
  const onReviewAction = useCallback(
    (sampleId?: string) => setShouldFetch(sampleId),
    [],
  );

  const clearLabelPropagationSearchParams =
    useClearLabelPropagationSearchParams();

  const handleViewChange = (view: RightPanelMode) => {
    switch (view) {
      case "summary":
      case "chat":
        setRightPanelMode(view);
        clearLabelPropagationSearchParams();

        return;
      case "labels":
        setRightPanelMode(view);
    }
  };
  const isReview = searchParams.has(REVIEW_PARAMETER);
  const dataType = isReview ? "review" : "data";
  const getDataByDataType: Record<DataListPageType, any> = {
    review: (
      <ReviewList
        setReviewCounters={setReviewCounters}
        onReviewAction={onReviewAction}
        onNavigateToCluster={onNavigateToCluster}
        labelPropagation={labelPropagationProps}
        updateNavigationCluster={updateNavigationCluster}
        reviewCounters={reviewCounters}
      />
    ),
    data: (
      <DataList
        page={dynamicPage}
        clusters={clusters || []}
        navigationCluster={navigationCluster}
        isContentLoading={dynamicLoader}
        showMetadataSummary={showMetadataSummary}
        labelPropagation={labelPropagationProps}
        isDataPage={!!datasetId && !clusterId}
        rightPanelMode={rightPanelMode}
      />
    ),
  };

  const innerBodyClassNames = classNames(styles.innerBody, {
    [styles.expandedInnerBody]: isReview,
  });

  const isDndEnabled =
    !searchParams.has(FLOW_PARAMETER) &&
    rightPanelMode === "labels" &&
    !isReview;

  const temporaryExportData = useSelector(getTemporaryExportData);

  const handleDragStart = (event: any) => {
    if (isDndEnabled) {
      const { current } = event.active.data;
      const data = current.flat_response ? current.previews[0] : current;
      // if dragging card that already select, don't de-select it, as updateTemporaryExportData removes it
      if (
        !temporaryExportData?.exportDataArray?.find((item) =>
          data.cluster_id
            ? item.cluster_id === data.cluster_id
            : item.media_id === data.media_id,
        )
      ) {
        const updateType = data.cluster_id ? "cluster" : "media";
        dispatch(
          updateTemporaryExportData(
            isDataPage ? data : { ...data, cluster_id: clusterId },
            updateType,
          ) as any,
        );
      }
      setActiveDragItem(event.active.data.current);
    }
  };

  const handleDragEnd = (event: any) => {
    if (isDndEnabled) {
      if (event.over) {
        labelPropagationProps.onLabelClick(event.over.data.current.classId);
      }
      dispatch(setTemporaryExportData(null));
      setActiveDragItem(null); // Reset the active ID
    }
  };

  const draggingItemsCount = temporaryExportData?.exportDataArray?.length || 0;

  const activeItem = activeDragItem ? (
    <div className={styles.dragOverlay}>
      {draggingItemsCount > 1 && (
        <div className={styles.counter}>{draggingItemsCount}</div>
      )}
      <EnrichmentCard
        {...activeDragItem.props}
        disableInteractivity
        className={styles.floatingCard}
      />
    </div>
  ) : null;

  return (
    <DndContext
      sensors={[
        useSensor(MouseSensor, {
          activationConstraint: { distance: 48 },
        }),
      ]}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      collisionDetection={pointerWithin}
    >
      <div
        className={styles.mainContainer}
        style={
          isCardListLoading && dataType === "data"
            ? { pointerEvents: "none" }
            : undefined
        }
      >
        <div className={styles.leftPanel}>
          {getDataPageFilters()}
          <div className={innerBodyClassNames}>
            {getDataByDataType[dataType]}
          </div>
          <QueryModalWrapper />
        </div>
        <div
          className={classNames(styles.rightPanel, {
            [styles.visible]: Boolean(rightPanelMode),
            [styles.labelPropagation]: rightPanelMode === "labels",
          })}
        >
          {showLabelPropagation && rightPanelMode !== "chat" && (
            <ViewToggleButtons
              showLabelPropagation={showLabelPropagation}
              currentView={rightPanelMode}
              onViewChange={handleViewChange}
              flywheelStatus={flywheelStatus}
            />
          )}
          {getRightPanel()}
        </div>
      </div>
      {isDndEnabled &&
        createPortal(<DragOverlay>{activeItem}</DragOverlay>, document.body)}
    </DndContext>
  );
};
export default CardsView;
